import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import logo from '../../..../../../../public/assets/logo.png';
import { Alert, Box, Button, Checkbox, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { spaceBetween } from '../../style/style';
import { PrimaryButon } from '../../components/common/button/PrimaryButon';
import { QInput } from '../../components/common/inputs/QInput';
import { black400, black500 } from '../../constants/color';
import { centered, loginContainer } from '../freelancer/account/style';
import { Controller, useForm } from 'react-hook-form';
import { checkShuftiProStatusAsync, loginAsync } from '@application/features/auth/authThunks';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { translate } from '@application/common/helpers/translate';
import { getCurrentUserFromJwt } from '@application/common/helpers/jwt-helper';
import { navigateBasedOnRoleAndVerification } from '@application/common/helpers/userRole-helper';
import { adminSessionStarted } from '@application/features/auth/authSlice';
import { AdminSecondStep, PasskeySignInButton } from './AdminSignInSteps';
export const Login = () => {
    const { apiError, currentUser } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const submitting = useRef(false);
    const ownsLoginFlow = useRef(false);
    const [loginError, setLoginError] = useState('');
    // Admin accounts with a passkey/authenticator app continue here after a correct password.
    const [adminStep, setAdminStep] = useState(null);
    // Strong admin sign-in (passkey or password + authenticator): the token stays in memory. A returnTo is followed
    // only through the same role-bound allowlist as the password sign-in.
    const finishStrongSignIn = (token) => {
        ownsLoginFlow.current = true;
        dispatch(adminSessionStarted(token));
        const signedInUser = getCurrentUserFromJwt(token);
        if (signedInUser)
            navigateBasedOnRoleAndVerification(signedInUser.role, undefined, navigate, location.state?.returnTo);
    };
    useEffect(() => {
        if (currentUser && !ownsLoginFlow.current) {
            navigate(['Admin', 'SuperAdmin'].includes(currentUser.role) ? '/home' : '/dashboard', { replace: true });
        }
    }, [currentUser, navigate]);
    const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm();
    const validatePassword = (password) => {
        if (password.length < 8)
            return 'Must contain atleast 8 characters.';
        if (!/[A-Z]+/.test(password))
            return 'Must contain atleast one uppercase letter.';
        if (!/[a-z]+/.test(password))
            return 'Must contain atleast one lowercase letter.';
        if (!/[0-9]+/.test(password))
            return 'Must contain atleast one number.';
        if (!/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password))
            return 'Must contain atleast one special character.';
    };
    const onSubmit = async (data) => {
        if (submitting.current)
            return;
        submitting.current = true;
        ownsLoginFlow.current = true;
        setLoginError('');
        try {
            const result = await dispatch(loginAsync(data)).unwrap();
            if (!result.accessToken && (result.secondFactorRequired || result.passkeyRequired)) {
                setAdminStep({ challengeId: result.challengeId, passkeyOnly: !!result.passkeyRequired && !result.secondFactorRequired });
                return;
            }
            const signedInUser = getCurrentUserFromJwt(result.accessToken);
            if (signedInUser) {
                const verified = await dispatch(checkShuftiProStatusAsync()).unwrap();
                navigateBasedOnRoleAndVerification(signedInUser.role, verified, navigate, location.state?.returnTo);
            }
        }
        catch {
            setLoginError('Sign in could not be completed. Please try again.');
        }
        finally {
            submitting.current = false;
        }
    };
    if (adminStep)
        return (_jsx(Box, { sx: { ...centered, minHeight: '100vh', py: 4, boxSizing: 'border-box' }, children: _jsxs(Box, { sx: { ...loginContainer }, children: [_jsx("img", { src: logo, alt: '', style: { width: 150, height: "auto" } }), _jsx(Typography, { fontSize: '34px', fontWeight: 500, mt: 3, color: '#000000', children: "Employee portal" }), _jsx(AdminSecondStep, { challengeId: adminStep.challengeId, passkeyOnly: adminStep.passkeyOnly, onSignedIn: finishStrongSignIn, onCancel: () => setAdminStep(null) })] }) }));
    return (_jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsx(Box, { sx: { ...centered, minHeight: '100vh', py: 4, boxSizing: 'border-box' }, children: _jsxs(Box, { sx: { ...loginContainer }, children: [_jsx("img", { src: logo, alt: '', style: { width: 150, height: "auto" } }), _jsx(Typography, { fontSize: '34px', fontWeight: 500, mt: 3, color: '#000000', children: "Employee portal" }), _jsx(Typography, { variant: 'subtitle1', color: black400, children: "Please enter your details to log in." }), loginError && apiError?.status !== 401 && _jsx(Alert, { severity: 'error', children: loginError }), apiError && apiError.status === 401 && (_jsx(Box, { style: {
                            color: '#842029',
                            backgroundColor: '#f8d7da',
                            borderColor: '#f5c2c7',
                            padding: '0.75rem 4.0rem',
                            marginTop: '15px',
                            border: '1px solid transparent',
                            borderRadius: '5.50rem'
                        }, role: 'alert', children: apiError.error })), _jsx(Controller, { name: 'email', control: control, defaultValue: '', rules: {
                            required: {
                                value: true,
                                message: translate('validations.fieldIsRequired')
                            },
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: translate('validations.invalidEmailAddress')
                            }
                        }, render: ({ field }) => (_jsx(QInput, { fillBackground: false, borderRadius: false, ...field, mt: 3, placeholder: 'Email Address', errormsg: errors.email ? errors.email.message : '' })) }), _jsx(Controller, { name: 'password', control: control, defaultValue: '', rules: {
                            required: {
                                value: true,
                                message: translate('validations.fieldIsRequired')
                            }
                        }, render: ({ field }) => (_jsx(QInput, { ...field, fillBackground: false, borderRadius: false, mt: 2.5, type: 'password', placeholder: 'Password', errormsg: errors.password ? errors.password.message : '' })) }), _jsxs(Box, { sx: { ...spaceBetween, mt: 2, width: '100%' }, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: [_jsx(Controller, { name: 'rememberMe', control: control, defaultValue: false, render: ({ field }) => _jsx(Checkbox, { ...field, sx: { color: '#D7D7D7' } }) }), _jsx(Typography, { variant: 'subtitle2', color: '#555555', children: translate('account.rememberMe') })] }), _jsx(Box, { children: _jsx(Button, { variant: 'text', sx: { fontWeight: 500 }, onClick: () => navigate('/forgot-password'), children: translate('account.forgotPassword') }) })] }), _jsx(PrimaryButon, { type: 'submit', mt: 20, disabled: isSubmitting, children: translate('account.signin') }), _jsx(PasskeySignInButton, { onSignedIn: finishStrongSignIn }), _jsxs(Box, { sx: { ...centered, mt: 2 }, children: [_jsx(Typography, { variant: 'subtitle1', color: black500, children: translate('account.dontHaveAnAccount') }), _jsx(Button, { onClick: () => navigate('/signup'), sx: { fontWeight: 500 }, children: translate('account.signup') })] })] }) }) }));
};
